import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { formatDate } from "../../../lib/date";
import { quoteEmailPrefill } from "../../../lib/quoteEmail";
import {
  Search, Download, Link2, ExternalLink, Plus, Send, Pencil, Trash2, Loader2, Copy,
  Sparkles, MoreVertical, Ban, CalendarClock, CheckCircle2, AlertTriangle, ChevronRight,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../../ui/dropdown-menu";
import { QuoteBuilder } from "./QuotesPanel";
import QuoteAssistant from "./QuoteAssistant";
import { formatMoney, formatMoney0 } from "../../../lib/money";
import { OverseasBadge } from "../OverseasBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 15;
const money2 = (v, cur = "AUD") => formatMoney(v, cur);
const money0 = (v, cur = "AUD") => formatMoney0(v, cur);
const SUFFIX = { one_off: "", monthly: "/mo", yearly: "/yr" };
const GROUP_LABEL = { one_off: "One-off", monthly: "Per month", yearly: "Per year" };

const QTONE = {
  draft: { quiet: true, dot: "#8A857A", label: "Draft" },
  sent: { fill: "#FFF7D1", border: "#E8D48A", ink: "#6B5600", dot: "#E0B800", label: "Sent" },
  accepted: { fill: "#E3F3E9", border: "#A9D9BE", ink: "#0B4F2E", dot: "#0E6B3F", label: "Accepted" },
  declined: { fill: "#FFE9E0", border: "#F3BFA8", ink: "#9A3200", dot: "#C23A00", label: "Declined" },
  expired: { fill: "#F1EEE6", border: "#E6E2D9", ink: "#8A857A", dot: "#B5AFA3", label: "Expired" },
  void: { fill: "#F1EEE6", border: "#E6E2D9", ink: "#8A857A", dot: "#B5AFA3", label: "Void" },
};
const CHIPS = ["all", "draft", "sent", "accepted", "declined", "expired"];
const CHIP_LABEL = { all: "All", draft: "Draft", sent: "Sent", accepted: "Accepted", declined: "Declined", expired: "Expired" };

const isLegacy = (n) => /^\d/.test(n || "");
const hasTotals = (t) => !!t && (t.monthly?.count || t.one_off?.count || t.yearly?.count);
const daysUntil = (s) => {
  if (!s) return null;
  const d = new Date(String(s).slice(0, 10)); const t = new Date();
  d.setHours(0, 0, 0, 0); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};

function StatusPill({ status }) {
  const t = QTONE[status] || QTONE.expired;
  if (t.quiet) return (
    <span className="inline-flex items-center gap-1.5"><span className="rounded-full" style={{ width: 5, height: 5, background: t.dot }} /><span className="text-[12.5px] text-[#59554D]">{t.label}</span></span>
  );
  return (
    <span className="inline-flex items-center gap-1.5" style={{ background: t.fill, color: t.ink, border: `1px solid ${t.border}`, borderRadius: 999, padding: "2px 9px" }}>
      <span className="rounded-full" style={{ width: 5, height: 5, background: t.dot }} /><span className="text-[10.5px] font-bold tracking-[0.04em]">{t.label}</span>
    </span>
  );
}

const GRID = "30px 158px minmax(210px,1.5fr) 148px 132px 112px 112px 112px 46px";

export default function QuotesOverview() {
  const navigate = useNavigate();
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE);
  const sentinelRef = useRef(null);
  const [catalogue, setCatalogue] = useState([]);
  const [settings, setSettings] = useState({});
  const [detail, setDetail] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderCtx, setBuilderCtx] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/quotes`);
      setAll(r.data.quotes || []);
    } catch (e) { toast.error("Failed to load quotes"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/quotes/catalogue`).then((r) => setCatalogue(r.data.catalogue || [])).catch(() => {});
    axios.get(`${API}/quotes/settings`).then((r) => setSettings(r.data.settings || {})).catch(() => {});
  }, []);
  useEffect(() => { setVisible(PAGE); }, [status, query]);

  const counts = useMemo(() => {
    const c = { all: all.length, draft: 0, sent: 0, accepted: 0, declined: 0, expired: 0 };
    for (const q of all) if (c[q.status] != null) c[q.status] += 1;
    return c;
  }, [all]);

  /* Currencies are never added together: the headline stays AUD and anything
     quoted in GBP/USD is shown as its own figure underneath. */
  const band = useMemo(() => {
    const b = { accMonthly: 0, accOneOff: 0, accCount: 0, outMonthly: 0, outOneOff: 0, sentCount: 0, byCur: {} };
    const bump = (cur, key, v) => {
      if (!v) return;
      b.byCur[cur] = b.byCur[cur] || { accMonthly: 0, accOneOff: 0, outMonthly: 0, outOneOff: 0 };
      b.byCur[cur][key] += v;
    };
    for (const q of all) {
      const cur = (q.currency || "AUD").toUpperCase();
      const m = q.totals?.monthly?.total || 0;
      const o = q.totals?.one_off?.total || 0;
      if (q.status === "accepted") {
        b.accCount++; bump(cur, "accMonthly", m); bump(cur, "accOneOff", o);
        if (cur === "AUD") { b.accMonthly += m; b.accOneOff += o; }
      }
      if (q.status === "sent") {
        b.sentCount++; bump(cur, "outMonthly", m); bump(cur, "outOneOff", o);
        if (cur === "AUD") { b.outMonthly += m; b.outOneOff += o; }
      }
    }
    return b;
  }, [all]);

  const foreign = useMemo(
    () => Object.entries(band.byCur).filter(([c]) => c !== "AUD"),
    [band]);

  const filtered = useMemo(() => {
    const qq = query.trim().toLowerCase();
    return all.filter((q) => {
      if (status !== "all" && q.status !== status) return false;
      if (!qq) return true;
      return [q.quote_number, q.link_name, q.contact_name].some((f) => (f || "").toLowerCase().includes(qq));
    });
  }, [all, status, query]);

  const paged = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;
  const draftCount = counts.draft;

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore) setVisible((v) => v + PAGE);
    }, { rootMargin: "200px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, filtered.length]);

  const fetchContacts = async (linkType, linkId) => {
    try {
      const url = linkType === "client"
        ? `${API}/customer-success/clients/${encodeURIComponent(linkId)}/contacts`
        : `${API}/sales/prospects/${encodeURIComponent(linkId)}/contacts`;
      const r = await axios.get(url); return r.data.contacts || [];
    } catch (e) { return []; }
  };
  const openEdit = async (q) => { navigate(`/quotes/${q.id}/edit`); };
  const startNew = async (linkType, linkId, linkName) => {
    const contacts = await fetchContacts(linkType, linkId);
    setBuilderCtx({ linkType, linkId, linkName, contacts, editingQuote: null });
    setPickerOpen(false); setBuilderOpen(true);
  };
  const downloadPdf = async (q) => {
    try {
      const res = await axios.get(`${API}/quotes/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${q.quote_number}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to download PDF"); }
  };
  const copyLink = (q) => { navigator.clipboard.writeText(`${window.location.origin}/quote/${q.public_token}`); toast.success("Public link copied"); };
  const sendQuote = (q) => {
    const route = quoteEmailPrefill(q);
    if (!route) { toast.error("This quote isn't linked to a client or prospect"); return; }
    navigate(route.dest, { state: route.state });
  };
  const voidQuote = async (q) => {
    if (!window.confirm(`Void quote ${q.quote_number}? The record is kept and the contact is notified. This cannot be undone.`)) return;
    setBusyId(q.id);
    try { await axios.post(`${API}/quotes/${q.id}/void`); toast.success(`${q.quote_number} voided`); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to void"); }
    finally { setBusyId(null); }
  };
  const del = async () => {
    const q = deleteTarget; if (!q) return; setDeleting(true);
    try { await axios.delete(`${API}/quotes/${q.id}`); toast.success("Deleted"); setDeleteTarget(null); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    finally { setDeleting(false); }
  };
  const duplicate = async (q) => {
    try { const r = await axios.post(`${API}/quotes/${q.id}/duplicate`); toast.success(`Duplicated → ${r.data.quote.quote_number}`); setDetail(null); load(); openEdit(r.data.quote); }
    catch (e) { toast.error("Failed to duplicate"); }
  };

  // selection + bulk
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const ids = paged.map((q) => q.id);
    const allSel = ids.every((id) => s.has(id));
    const n = new Set(s); ids.forEach((id) => allSel ? n.delete(id) : n.add(id)); return n;
  });
  const selectedQuotes = all.filter((q) => selected.has(q.id));
  const bulkRun = async (verb, fn) => {
    let ok = 0, fail = 0;
    for (const q of selectedQuotes) {
      try { await fn(q); ok++; } catch (e) { fail++; }
    }
    toast[fail && !ok ? "error" : "success"](`${verb}: ${ok} done${fail ? `, ${fail} skipped` : ""}`);
    setSelected(new Set()); load();
  };

  const clearFilters = () => { setStatus("all"); setQuery(""); };

  return (
    <div style={{ padding: "0", display: "flex", flexDirection: "column", gap: 14 }} data-testid="quotes-overview">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-[25px] font-extrabold text-[#1A1A1A] tracking-[-0.018em]" style={{ fontFamily: "Manrope, sans-serif" }}>Quotes</h1>
          <p className="text-[13px] text-[#59554D] mt-0.5 leading-[1.6]">
            Every quote across clients and prospects. <span className="mono text-[11.5px] text-[#1A1A1A]">{counts.sent}</span> out with customers, <span className="mono text-[11.5px] text-[#1A1A1A]">{draftCount}</span> still in draft.
          </p>
        </div>
        <Button onClick={() => navigate("/quotes/new")} data-testid="quotes-new-btn"
          className="h-[34px] bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[12px] tracking-[0.04em] font-bold" style={{ border: "1px solid #E0B800" }}>
          <Plus className="w-4 h-4 mr-2" /> New quote
        </Button>
      </div>

      {/* Money band */}
      <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", background: "#E6E2D9", gap: 1 }}>
            {[
              ["Accepted · recurring", money0(band.accMonthly), "per month", `across ${band.accCount} accepted quotes`,
                foreign.map(([c, v]) => `${money0(v.accMonthly, c)} /mo`).filter((x) => !x.startsWith("A$0") && !x.startsWith("£0") && !x.startsWith("US$0")).join(" · ")],
              ["Accepted · one-off", money0(band.accOneOff), null, "implementation and setup",
                foreign.map(([c, v]) => money0(v.accOneOff, c)).filter((x) => !/0$/.test(x)).join(" · ")],
              ["Out with customers", money0(band.outMonthly), "per month", `plus ${money0(band.outOneOff)} one-off, across ${band.sentCount} sent quotes`,
                foreign.map(([c, v]) => `${money0(v.outMonthly, c)} /mo`).filter((x) => !/0 \/mo$/.test(x)).join(" · ")],
            ].map(([o, v, u, s, fx]) => (
              <div key={o} className="bg-white p-4">
                <div className="text-[10px] uppercase tracking-[0.09em] text-[#6B6660] font-medium mb-1.5">{o}</div>
                <div className="flex items-baseline gap-1.5"><span className="text-[23px] font-extrabold text-[#1A1A1A] tracking-[-0.02em]" style={{ fontFamily: "Manrope, sans-serif" }}>{v}</span>{u && <span className="text-[12.5px] text-[#59554D]">{u}</span>}</div>
                <div className="text-[12.5px] text-[#59554D] mt-1.5">{s}</div>
                {fx ? <div className="text-[11.5px] mt-1 font-semibold" style={{ color: "#6B5600" }} data-testid="quotes-band-fx">plus {fx}</div> : null}
              </div>
            ))}
          </div>
          <div className="flex items-start gap-2 px-4 py-2.5" style={{ background: "#F6F4EF", borderTop: "1px solid #E6E2D9" }}>
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: "#C23A00" }} />
            <p className="text-[11.5px] text-[#59554D] leading-[1.5]">Win rate is not shown here. No quote has ever been marked declined or expired, so every decided quote reads as won. <span className="font-semibold text-[#6B5600]">See Sales overview</span> for the full list of withheld figures.</p>
          </div>
        </CardContent>
      </Card>

      {/* Register card */}
      <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
        <CardContent className="p-0">
          {/* Toolbar band */}
          <div className="flex items-center gap-2.5 flex-wrap" style={{ padding: "13px 14px", borderBottom: "1px solid #E6E2D9" }}>
            <div className="relative" style={{ flex: "1 1 280px", minWidth: 240, maxWidth: 520 }}>
              <Search className="w-4 h-4 text-[#8A857A] absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Quote number, company or contact" data-testid="quotes-search"
                className="w-full h-[42px] pl-9 pr-16 rounded-[9px] text-[13px] text-[#1A1A1A] outline-none" style={{ border: "1.5px solid #1A1A1A", background: "#fff" }} />
              <span className="mono text-[10.5px] text-[#8A857A] absolute right-3 top-1/2 -translate-y-1/2">{query ? `${filtered.length} of ${all.length}` : all.length}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {CHIPS.map((s) => {
                const n = counts[s] ?? 0;
                const inert = s !== "all" && n === 0;
                const active = status === s;
                const t = QTONE[s];
                return (
                  <button key={s} disabled={inert} data-testid={`quotes-filter-${s}`}
                    onClick={() => setStatus(active ? "all" : s)}
                    className="inline-flex items-center gap-1.5 h-7 rounded-full transition-colors"
                    style={{ padding: "0 10px", background: active ? "#FFF8D6" : "#fff", border: `1px solid ${active ? "#E0B800" : "#DAD5CA"}`, cursor: inert ? "default" : "pointer" }}>
                    {s !== "all" && <span className="rounded-full" style={{ width: 6, height: 6, background: inert ? "#B5AFA3" : (t?.dot || "#8A857A") }} />}
                    <span className="text-[12px]" style={{ color: inert ? "#B5AFA3" : "#59554D", fontWeight: active ? 600 : 400 }}>{CHIP_LABEL[s]}</span>
                    <span className="mono text-[10.5px]" style={{ color: inert ? "#B5AFA3" : "#8A857A" }}>{n}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Assistant strip */}
          <div style={{ borderBottom: "1px solid #E6E2D9" }}>
            {!assistantOpen ? (
              <button onClick={() => setAssistantOpen(true)} data-testid="quotes-assistant-open"
                className="w-full flex items-center gap-2 h-[38px] px-4 transition-colors hover:bg-[#F4F2ED]" style={{ background: "#FBFAF7" }}>
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#E0B800" }} />
                <span className="text-[12.5px] font-semibold text-[#1A1A1A]">Quote assistant</span>
                <span className="text-[11.5px] text-[#8A857A]">Query quotes, or describe one to draft it</span>
                <span className="ml-auto text-[11.5px] font-semibold" style={{ color: "#6B5600" }}>Open</span>
              </button>
            ) : (
              <div className="px-4 py-3" style={{ background: "#FBFAF7" }}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11.5px] text-[#8A857A]">A described quote opens in the builder as a draft. Nothing is created or sent from here.</span>
                  <button onClick={() => setAssistantOpen(false)} className="text-[11.5px] font-semibold" style={{ color: "#6B5600" }} data-testid="quotes-assistant-close">Close</button>
                </div>
                <QuoteAssistant onCreated={load} />
              </div>
            )}
          </div>

          {/* Bulk bar */}
          {selected.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap" style={{ background: "#FFF8D6", borderBottom: "1px solid #EFE0A0", padding: "9px 14px" }} data-testid="quotes-bulk-bar">
              <span className="text-[12.5px] font-semibold text-[#1A1A1A]">{selected.size} quote{selected.size > 1 ? "s" : ""} selected</span>
              <span className="text-[#D9C97A]">|</span>
              {[
                ["Email to contact", selected.size === 1
                  ? "Opens the email with the quote in it, for you to read before it goes"
                  : "An email is read before it goes, so select one quote",
                  selected.size !== 1,
                  () => { const q = filtered.find((x) => selected.has(x.id)); if (q) sendQuote(q); },
                  selected.size === 1 ? "#59554D" : "#B5AFA3"],
                ["Mark accepted", `Records acceptance on ${selected.size} quotes`, false, () => bulkRun("Accepted", (q) => axios.post(`${API}/quotes/${q.id}/accept-manual`)), "#59554D"],
                ["Extend validity", `Moves the valid-until date on ${selected.size} quotes`, false, () => bulkRun("Extended", (q) => axios.post(`${API}/quotes/${q.id}/extend`)), "#59554D"],
                ["Export PDFs", "Batch PDF rendering is not live", true, null, "#B5AFA3"],
                ["Void", `Voids ${selected.size} quotes and notifies their contacts`, false, () => bulkRun("Voided", (q) => axios.post(`${API}/quotes/${q.id}/void`)), "#8E1212"],
              ].map(([label, hint, disabled, fn, ink]) => (
                <button key={label} title={hint} disabled={disabled} onClick={fn || undefined} data-testid={`quotes-bulk-${label.split(" ")[0].toLowerCase()}`}
                  className="h-7 px-2.5 rounded-md text-[12px] font-semibold transition-colors" style={{ background: "#fff", border: "1px solid #DCCF95", color: ink, cursor: disabled ? "default" : "pointer", opacity: 1 }}>
                  {label}
                </button>
              ))}
              <button onClick={() => setSelected(new Set())} className="ml-auto text-[12px] underline text-[#59554D]" data-testid="quotes-bulk-clear">Clear</button>
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto">
            <div style={{ minWidth: 1230 }}>
              <div className="grid items-center px-3" style={{ gridTemplateColumns: GRID, columnGap: 10, height: 36, background: "#FBFAF7", borderBottom: "1px solid #E6E2D9" }}>
                <span><input type="checkbox" checked={paged.length > 0 && paged.every((q) => selected.has(q.id))} onChange={toggleAll} data-testid="quotes-select-all" style={{ width: 14, height: 14 }} /></span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#1A1A1A] font-bold">Quote #</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">For</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Contact</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Status</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium text-right">Per month</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium text-right">One-off</span>
                <span className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium text-right">Valid until</span>
                <span />
              </div>

              {loading ? (
                Array.from({ length: 8 }).map((_, i) => <div key={i} style={{ background: i % 2 ? "#fff" : "#FBFAF7" }} className="px-3 py-3 border-b border-[#F0EDE6]"><div className="skeleton h-4 w-full" /></div>)
              ) : filtered.length === 0 ? (
                <div className="px-6 py-14 text-center" data-testid="quotes-empty">
                  {all.length === 0 ? (
                    <><p className="text-[15px] font-semibold text-[#1A1A1A]">No quotes yet.</p><Button onClick={() => navigate("/quotes/new")} className="mt-3 h-[34px] bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] uppercase text-[12px] font-bold" style={{ border: "1px solid #E0B800" }}>New quote</Button></>
                  ) : (
                    <><p className="text-[15px] font-semibold text-[#1A1A1A]">No quotes match these filters.</p><Button variant="outline" onClick={clearFilters} className="mt-3 h-8 bg-white border-[#C9C3B6] text-[#59554D]" data-testid="quotes-clear-filters">Clear filters</Button></>
                  )}
                </div>
              ) : paged.map((q) => {
                const exc = !hasTotals(q.totals);
                const du = q.status === "sent" ? daysUntil(q.valid_until) : null;
                const expiring = du != null && du <= 14;
                const sel = selected.has(q.id);
                return (
                  <div key={q.id} onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`quotes-row-${q.id}`}
                    className="grid items-center px-3 cursor-pointer transition-colors border-b border-[#F0EDE6] hover:bg-[#FFFCEF]"
                    style={{ gridTemplateColumns: GRID, columnGap: 10, minHeight: 48, padding: "7px 12px", background: sel ? "#FFF8D6" : "#fff", boxShadow: sel ? "inset 3px 0 0 #E0B800" : exc ? "inset 3px 0 0 #FF4F00" : "none" }}>
                    <span onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel} onChange={() => toggle(q.id)} data-testid={`quotes-select-${q.id}`} style={{ width: 14, height: 14 }} /></span>
                    <div className="min-w-0">
                      <div className="mono text-[11.5px] font-bold text-[#1A1A1A] truncate">{q.quote_number}</div>
                      {isLegacy(q.quote_number) && <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-[#59554D]">Legacy id</div>}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <span className="text-[13px] font-semibold text-[#6B5600] truncate">{q.link_name || q.link_id}</span>
                      <OverseasBadge currency={q.currency} testid={`quote-overseas-${q.id}`} />
                        <span className="text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded shrink-0" style={q.link_type === "prospect" ? { background: "#FFF7D1", border: "1px solid #E8D48A", color: "#6B5600" } : { background: "#F1EEE6", border: "1px solid #DAD5CA", color: "#59554D" }}>{q.link_type}</span>
                      </div>
                      {exc && <div className="inline-flex items-center gap-1 mt-1 text-[10px] font-bold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded" style={{ background: "#FFEDE4", border: "1px solid #FFC9AE", color: "#8F2E00" }}>No totals recorded</div>}
                    </div>
                    <div className="min-w-0">{q.contact_name ? <span className="text-[12.5px] text-[#59554D] truncate block">{q.contact_name}</span> : <span className="text-[12.5px] italic text-[#B5AFA3]">No contact</span>}</div>
                    <div><StatusPill status={q.status} /></div>
                    <div className="mono text-[11.5px] text-right" style={{ fontWeight: 500 }}>{q.totals?.monthly?.count ? <span className="text-[#1A1A1A]">{money2(q.totals.monthly.total, q.currency)}</span> : <span className="text-[#C9C3B6]">—</span>}</div>
                    <div className="mono text-[11.5px] text-right" style={{ fontWeight: 400 }}>{q.totals?.one_off?.count ? <span className="text-[#1A1A1A]">{money2(q.totals.one_off.total, q.currency)}</span> : <span className="text-[#C9C3B6]">—</span>}</div>
                    <div className="text-right">
                      {q.valid_until ? (
                        <>
                          <div className="mono text-[11.5px]" style={{ color: expiring ? "#9A3200" : "#59554D", fontWeight: expiring ? 500 : 400 }}>{formatDate(q.valid_until)}</div>
                          {expiring && <div className="mono text-[10px]" style={{ color: "#9A3200" }}>{du >= 0 ? `expires in ${du}d` : `expired ${-du}d ago`}</div>}
                        </>
                      ) : <span className="mono text-[11.5px] text-[#C9C3B6]">—</span>}
                    </div>
                    <div onClick={(e) => e.stopPropagation()} className="flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="w-[30px] h-[30px] rounded-md flex items-center justify-center text-[#6B6660] hover:bg-[#F4F2ED]" data-testid={`quotes-menu-${q.id}`}><MoreVertical className="w-4 h-4" /></button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[248px] bg-[#FBFAF7] border-[#DAD5CA] rounded-xl p-1.5" style={{ boxShadow: "var(--shadow-pop)" }}>
                          <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => navigate(`/quotes/${q.id}`)}><ExternalLink className="w-[15px] h-[15px] text-[#6B6660]" /> Open quote</DropdownMenuItem>
                          {q.status === "draft" && <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openEdit(q)}><Pencil className="w-[15px] h-[15px] text-[#6B6660]" /> Edit in builder</DropdownMenuItem>}
                          <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => sendQuote(q)} data-testid={`quote-email-${q.id}`}><Send className="w-[15px] h-[15px] text-[#6B6660]" /> Email to contact</DropdownMenuItem>
                          <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => downloadPdf(q)}><Download className="w-[15px] h-[15px] text-[#6B6660]" /> Download PDF</DropdownMenuItem>
                          <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => copyLink(q)}><Link2 className="w-[15px] h-[15px] text-[#6B6660]" /> Copy link</DropdownMenuItem>
                          <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => duplicate(q)}><Copy className="w-[15px] h-[15px] text-[#6B6660]" /> Duplicate</DropdownMenuItem>
                          <DropdownMenuSeparator className="bg-[#E6E2D9]" />
                          {q.status === "draft" ? (
                            <DropdownMenuItem className="rounded-lg items-start gap-2 py-2" style={{ color: "#8E1212" }} onClick={() => setDeleteTarget(q)}>
                              <Trash2 className="w-[15px] h-[15px] mt-0.5" style={{ color: "#8E1212" }} /><span><span className="text-[13px] font-semibold block">Delete quote</span><span className="text-[11.5px] font-normal text-[#59554D]">Only drafts can be deleted.</span></span>
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem className="rounded-lg items-start gap-2 py-2" style={{ color: "#8E1212" }} onClick={() => voidQuote(q)}>
                              <Ban className="w-[15px] h-[15px] mt-0.5" style={{ color: "#8E1212" }} /><span><span className="text-[13px] font-semibold block">Void quote</span><span className="text-[11.5px] font-normal text-[#59554D]">Marks it void and notifies the contact. Cannot be undone.</span></span>
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                );
              })}
              {!loading && filtered.length > 0 && (
                <div ref={sentinelRef} className="py-4 text-center" data-testid="quotes-scroll-sentinel">
                  {hasMore ? (
                    <Button variant="ghost" size="sm" onClick={() => setVisible((v) => v + PAGE)} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="quotes-load-more">Load more</Button>
                  ) : (
                    <span className="text-xs text-[#C9C3B6]">End of list</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 flex-wrap px-3.5 py-[11px] bg-[#FBFAF7] border-t border-[#E6E2D9]">
            <span className="mono text-[11.5px] text-[#59554D]">{loading ? "Loading…" : `${filtered.length} of ${all.length} quotes`}</span>
            <span className="text-[11px] text-[#8A857A]">Scroll to load more · page size {PAGE}</span>
          </div>
        </CardContent>
      </Card>

      {/* Detail Sheet (Part C deferred) */}
      <Sheet open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <SheetContent side="right" className="bg-[#FBFAF7] border-[#DAD5CA] w-full sm:max-w-xl overflow-y-auto p-0" data-testid="quote-detail-sheet">
          {detail && (
            <div>
              <SheetHeader className="px-6 py-4 border-b border-[#E6E2D9] bg-white">
                <SheetTitle className="flex items-center gap-2"><span className="mono text-lg text-[#1A1A1A]">{detail.quote_number}</span><StatusPill status={detail.status} /></SheetTitle>
                <p className="text-sm text-[#59554D] text-left">{detail.link_type === "client" ? "Client" : "Prospect"} · {detail.link_name} · {detail.contact_name || "no contact"}</p>
              </SheetHeader>
              <div className="px-6 py-3 flex flex-wrap gap-2 border-b border-[#E6E2D9] bg-white">
                {detail.status === "draft" && <Button size="sm" onClick={() => openEdit(detail)} className="bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A]" data-testid="quote-detail-edit"><Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>}
                <Button size="sm" variant="outline" onClick={() => sendQuote(detail)} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="quote-detail-send"><Send className="w-3.5 h-3.5 mr-1.5" /> Email</Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(detail)} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="quote-detail-pdf"><Download className="w-3.5 h-3.5 mr-1.5" /> PDF</Button>
                <Button size="sm" variant="outline" onClick={() => copyLink(detail)} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="quote-detail-link"><Link2 className="w-3.5 h-3.5 mr-1.5" /> Copy link</Button>
                <Button size="sm" variant="outline" onClick={() => duplicate(detail)} className="border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]" data-testid="quote-detail-duplicate"><Copy className="w-3.5 h-3.5 mr-1.5" /> Duplicate</Button>
                {detail.status === "draft"
                  ? <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(detail)} className="text-[#8E1212] hover:bg-[#FFE9E0]" data-testid="quote-detail-delete"><Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete</Button>
                  : <Button size="sm" variant="ghost" onClick={() => voidQuote(detail)} className="text-[#8E1212] hover:bg-[#FFE9E0]" data-testid="quote-detail-void"><Ban className="w-3.5 h-3.5 mr-1.5" /> Void</Button>}
              </div>
              <div className="px-6 py-4 space-y-4">
                <div className="flex justify-between text-sm"><span className="text-[#59554D]">Valid until</span><span className="text-[#1A1A1A]">{detail.valid_until ? formatDate(detail.valid_until) : "—"}</span></div>
                {detail.status === "accepted" && <div className="rounded-lg bg-[#E9F6EE] border border-[#BFE3CD] px-3 py-2 text-sm text-[#0E6B3F]">Accepted{detail.responded_by ? ` by ${detail.responded_by}` : ""}{detail.accepted_at ? ` · ${formatDate(detail.accepted_at)}` : ""}</div>}
                {detail.status === "declined" && <div className="rounded-lg bg-[#FBEAE3] border border-[#F0C7B4] px-3 py-2 text-sm text-[#C23A00]">Declined{detail.response_comment ? ` · “${detail.response_comment}”` : ""}</div>}
                {["monthly", "one_off", "yearly"].map((g) => {
                  const rows = (detail.line_items || []).filter((l) => (l.billing || "one_off") === g);
                  if (!rows.length) return null;
                  const t = detail.totals?.[g] || {};
                  return (
                    <div key={g}>
                      <div className="text-[11px] font-bold uppercase tracking-wide text-[#8A6D00] mb-1.5">{GROUP_LABEL[g]}</div>
                      <div className="rounded-lg border border-[#E6E2D9] bg-white overflow-hidden">
                        {rows.map((l, i) => (<div key={i} className="flex items-center justify-between px-3 py-2 border-b border-[#F0EDE6] text-sm"><span className="text-[#1A1A1A] truncate">{l.description} <span className="text-[#8A857A]">× {l.qty}</span></span><span className="mono text-[#1A1A1A]">{money2((l.qty || 0) * (l.unit_price || 0), detail.currency)}</span></div>))}
                        <div className="flex items-center justify-between px-3 py-1.5 text-[13px] text-[#59554D] bg-[#FBFAF7]"><span>Subtotal</span><span className="mono">{money2(t.subtotal, detail.currency)}</span></div>
                        <div className="flex items-center justify-between px-3 py-1.5 text-[13px] text-[#59554D] bg-[#FBFAF7]"><span>{detail?.tax_label || (detail?.tax_treatment === "export" ? "No Tax Applicable" : "GST 10%")}</span><span className="mono">{money2(t.gst, detail.currency)}</span></div>
                        <div className="flex items-center justify-between px-3 py-2 text-sm font-semibold text-[#1A1A1A] bg-[#FBFAF7]"><span>{detail?.tax_treatment === "export" ? "Total" : "Total incl GST"}</span><span className="mono">{money2(t.total, detail.currency)}{SUFFIX[g]}</span></div>
                      </div>
                    </div>
                  );
                })}
                {detail.notes && <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-1">Notes</div><p className="text-sm text-[#59554D] whitespace-pre-wrap">{detail.notes}</p></div>}
                {detail.terms && <div><div className="text-[11px] font-bold uppercase tracking-wide text-[#8A857A] mb-1">Terms</div><p className="text-xs text-[#8A857A] whitespace-pre-wrap">{detail.terms}</p></div>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <NewQuotePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={startNew} />
      <DeleteQuoteDialog quote={deleteTarget} busy={deleting} onClose={() => { if (!deleting) setDeleteTarget(null); }} onConfirm={del} />
      {builderCtx && (
        <QuoteBuilder open={builderOpen} onClose={() => setBuilderOpen(false)} catalogue={catalogue} settings={settings} contacts={builderCtx.contacts} linkType={builderCtx.linkType} linkId={builderCtx.linkId} linkName={builderCtx.linkName} editingQuote={builderCtx.editingQuote} onSaved={() => load()} />
      )}
    </div>
  );
}

// ── Delete confirmation ──
export function DeleteQuoteDialog({ quote, busy, onClose, onConfirm }) {
  return (
    <Dialog open={!!quote} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="quote-delete-confirm">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="w-4 h-4 text-[#C23A00]" /> Delete this draft?</DialogTitle></DialogHeader>
        {quote && (<div className="space-y-3 text-sm"><p className="text-[#59554D]">Draft <span className="mono font-semibold text-[#1A1A1A]">{quote.quote_number}</span>{quote.link_name ? <> for <span className="font-medium text-[#1A1A1A]">{quote.link_name}</span></> : null} will be permanently deleted. This can’t be undone.</p></div>)}
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="ghost" onClick={onClose} disabled={busy} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="quote-delete-cancel">Cancel</Button>
          <Button onClick={onConfirm} disabled={busy} className="bg-[#C23A00] hover:bg-[#A83100] text-white" data-testid="quote-delete-confirm-btn">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />} Delete</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── New quote link picker (Part B builder deferred) ──
function NewQuotePicker({ open, onClose, onPick }) {
  const [type, setType] = useState("client");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true); setQ("");
    const url = type === "client" ? `${API}/customer-success/clients` : `${API}/sales/prospects?limit=500`;
    axios.get(url).then((r) => {
      const list = type === "client" ? (r.data.clients || []).map((c) => ({ id: c.client_id, name: c.company_name })) : (r.data.prospects || []).map((p) => ({ id: p.id, name: p.company_name }));
      setItems(list);
    }).catch(() => setItems([])).finally(() => setLoading(false));
  }, [open, type]);
  const filtered = items.filter((it) => (it.name || "").toLowerCase().includes(q.toLowerCase())).slice(0, 100);
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white border-[#DAD5CA] text-[#1A1A1A] max-w-md" data-testid="quote-picker">
        <DialogHeader><DialogTitle>New quote — choose a record</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-2">{["client", "prospect"].map((t) => (<button key={t} onClick={() => setType(t)} className={`h-8 px-3 rounded-full text-[11px] font-semibold uppercase tracking-wide ${type === t ? "bg-[#1A1A1A] text-white" : "bg-[#F4F2ED] text-[#59554D] hover:bg-[#EBE8E1]"}`} data-testid={`quote-picker-type-${t}`}>{t}</button>))}</div>
        <div className="relative mb-2"><Search className="w-4 h-4 text-[#8A857A] absolute left-3 top-1/2 -translate-y-1/2" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${type}s`} className="pl-9 bg-[#F4F2ED] border-[#DAD5CA]" data-testid="quote-picker-search" autoFocus /></div>
        <div className="max-h-[320px] overflow-y-auto rounded-lg border border-[#E6E2D9]">
          {loading ? (<div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin text-[#8A6D00] mx-auto" /></div>) : filtered.length === 0 ? (<div className="p-6 text-center text-sm text-[#8A857A]">No {type}s found.</div>) : filtered.map((it) => (<button key={it.id} onClick={() => onPick(type, it.id, it.name)} className="w-full text-left px-3 py-2 border-b border-[#F0EDE6] hover:bg-[#FFFCEF] text-sm text-[#1A1A1A] flex items-center justify-between" data-testid={`quote-picker-item-${it.id}`}>{it.name}<ChevronRight className="w-3.5 h-3.5 text-[#B5AFA3]" /></button>))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
